import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BIS_PIECES, MOLD_FIELD_LABELS, MOLD_ROOM_FILES_MIN, moldFieldLabel, type MoldAnswers, type MoldQuestion, type MoldReading } from '@raro/domain';
import { Button, cx, Dropzone, Input, Label, Lightbox, MoldSvg, Notice, Select, Serif, Tag, Textarea, fillMold, moldProblem } from '@raro/ui';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { plural } from './types';

interface RoomRow { id: string; title: string; piece: string; pieceLabel: string; status: 'open' | 'done'; files: number; fields: number; updatedAt: string }
interface RoomFont { family: string; weight: number; italic: boolean; data: string }
interface Room {
  id: string; title: string; piece: string; pieceLabel: string; page: number; status: 'open' | 'done'; files: string[];
  reading: MoldReading | null; questions: MoldQuestion[]; answers: MoldAnswers; notes: string; notices: string[]; fonts: RoomFont[]; inBis: boolean;
}

/**
 * A sala de moldes do BIS (fase 1: peças lidas de PDF). O designer sobe vários exemplares finais da mesma peça; a leitura
 * compara um com o outro e separa o desenho dos campos; a sala pergunta o que ela não tem como saber; a prova mostra o
 * molde preenchido ao lado do original. Concluir leva o molde ao capítulo visual do BIS, como bloco, para publicar.
 */
export function MoldRooms({ organizationId, qs, brandId, onBis }: { organizationId: string; qs: string; brandId?: string; onBis: () => void }) {
  const qc = useQueryClient();
  const listKey = ['mold-rooms', organizationId, brandId ?? ''];
  const list = useQuery({ queryKey: listKey, queryFn: () => api.get<RoomRow[]>(`/organizations/${organizationId}/bis/mold-rooms${qs}`) });
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const rows = list.data ?? [];
  useEffect(() => { if (open === null && list.data) setOpen(list.data[0]?.id ?? 'new'); }, [list.data, open]);
  const refresh = () => void qc.invalidateQueries({ queryKey: listKey });

  return (
    <div className="rr-moldroom">
      <aside className="rr-bisbuild__nav">
        <Label>moldes</Label>
        {rows.map((r) => (
          <button key={r.id} type="button" className={cx('rr-bisbuild__chapter', open === r.id && 'rr-bisbuild__chapter--on')} onClick={() => setOpen(r.id)}>
            <span>{r.status === 'done' ? '●' : '○'}</span>
            <b>{r.title}</b>
            <small>{r.pieceLabel} · {plural(r.files, 'arquivo', 'arquivos')} · {r.status === 'done' ? 'no BIS' : 'em aberto'}</small>
          </button>
        ))}
        <button type="button" className={cx('rr-bisbuild__chapter', open === 'new' && 'rr-bisbuild__chapter--on')} onClick={() => setOpen('new')}><span>+</span><b>novo molde</b><small>a partir dos arquivos finais</small></button>
        <div className="rr-bisbuild__box">
          <Label>como funciona</Label>
          <small>suba vários exemplares da mesma peça, prontos, em PDF com o texto vivo. o que muda de um para o outro vira campo; o que é igual vira desenho.</small>
          <small>a leitura é regra, sem modelo. o que ela não tem como saber, ela pergunta.</small>
          <small>o molde só chega ao MODO depois que um sócio publica a versão do BIS.</small>
        </div>
      </aside>
      {open === 'new' || !open
        ? <NewRoom organizationId={organizationId} brandId={brandId} onCreated={(id) => { refresh(); setOpen(id); }} />
        : <RoomView key={open} id={open} onChanged={refresh} onDeleted={() => { setOpen(null); refresh(); }} onBis={onBis} />}
    </div>
  );
}

function NewRoom({ organizationId, brandId, onCreated }: { organizationId: string; brandId?: string; onCreated: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [piece, setPiece] = useState('cartao');
  const [page, setPage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const create = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('piece', piece);
      if (page) form.append('page', page);
      if (brandId) form.append('brandId', brandId);
      for (const f of files) form.append('files', f);
      return api.post<Room>(`/organizations/${organizationId}/bis/mold-rooms`, form);
    },
    onSuccess: (r) => onCreated(r.id),
  });
  const pdfs = files.filter((f) => /\.pdf$/i.test(f.name));
  return (
    <main className="rr-moldroom__new">
      <header><h2>novo molde</h2><p>um molde é o desenho de uma peça com os campos marcados. ele sai dos arquivos que a agência já finalizou.</p></header>
      <div className="rr-moldroom__form">
        <label><Label>nome do molde</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="cartão de visita, frente" /></label>
        <label><Label>tipo de peça</Label><Select value={piece} onChange={(e) => setPiece(e.target.value)}>{BIS_PIECES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</Select></label>
        <label><Label>página da peça no PDF</Label><Input value={page} onChange={(e) => setPage(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" placeholder="a que tem os dados" /></label>
      </div>
      <Dropzone files={files} onFiles={setFiles} accept="application/pdf,.pdf" hint={`pelo menos ${MOLD_ROOM_FILES_MIN} exemplares da mesma peça, em PDF, sem converter o texto em curvas. quanto mais exemplares, melhor a leitura`} />
      {files.length > pdfs.length && <Notice>há arquivo que não é PDF: nesta fase a sala lê só PDF. Photoshop, carrossel e foto entram na fase seguinte.</Notice>}
      {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
      <div><Button variant="primary" disabled={create.isPending || title.trim().length < 2 || pdfs.length < MOLD_ROOM_FILES_MIN || pdfs.length !== files.length} onClick={() => create.mutate()}>{create.isPending ? 'lendo os arquivos…' : `ler ${plural(files.length, 'arquivo', 'arquivos')}`}</Button></div>
    </main>
  );
}

/** Registra as fontes da marca no navegador: a prova mede o texto, então só vale com a fonte certa carregada. */
function useFonts(fonts: RoomFont[] | undefined) {
  const [ready, setReady] = useState(0);
  useEffect(() => {
    let alive = true;
    void Promise.all((fonts ?? []).map(async (f) => {
      try { const face = new FontFace(f.family, `url(${f.data})`, { weight: String(f.weight), style: f.italic ? 'italic' : 'normal' }); await face.load(); document.fonts.add(face); } catch { /* arquivo que o navegador não abre: a prova usa a substituta, e o aviso da sala diz */ }
    })).then(() => { if (alive) setReady((n) => n + 1); });
    return () => { alive = false; };
  }, [fonts]);
  return ready;
}

function RoomView({ id, onChanged, onDeleted, onBis }: { id: string; onChanged: () => void; onDeleted: () => void; onBis: () => void }) {
  const qc = useQueryClient();
  const key = ['mold-room', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<Room>(`/bis-mold-rooms/${id}`) });
  const room = q.data;
  const [answers, setAnswers] = useState<MoldAnswers>({});
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (room) { setAnswers(room.answers); setNotes(room.notes); setDirty(false); } }, [room]);
  const set = (r: Room) => { qc.setQueryData(key, r); onChanged(); };
  const save = useMutation({ mutationFn: () => api.post<Room>(`/bis-mold-rooms/${id}/answers`, { answers, notes }), onSuccess: set });
  const finish = useMutation({ mutationFn: () => api.post(`/bis-mold-rooms/${id}/finish`), onSuccess: () => { void qc.invalidateQueries({ queryKey: key }); onChanged(); } });
  const remove = useMutation({ mutationFn: () => api.delete(`/bis-mold-rooms/${id}`), onSuccess: onDeleted });
  const fontsKey = useFonts(room?.fonts);
  const [test, setTest] = useState<Record<string, string>>({});
  const [zoom, setZoom] = useState<number | null>(null);

  if (!room) return <main className="rr-moldroom__new"><p className="rr-faint">{q.isError ? (q.error as Error).message : 'abrindo a sala…'}</p></main>;
  const r = room.reading;
  if (!r) return <main className="rr-moldroom__new"><Notice tone="error">esta sala não tem leitura. apague e abra de novo com os arquivos.</Notice></main>;
  const answered = (qn: MoldQuestion) => answers[qn.id] ?? qn.suggested;
  const unnamed = r.fields.filter((f) => /^campo\d+$/.test(f.field));
  const testing = Object.values(test).some((v) => v.trim());
  const previews = r.previews.filter((p) => p.image);

  return (
    <>
      <main className="rr-moldroom__main">
        <header>
          <div><h2>{room.title}</h2><p>{room.pieceLabel} · {plural(room.files.length, 'arquivo comparado', 'arquivos comparados')} · página {room.page}</p></div>
          <span>{room.status === 'done' && <Tag tone="accent">no BIS</Tag>}<button type="button" className="rr-action rr-action--bare" disabled={remove.isPending} onClick={() => remove.mutate()}>apagar a sala</button></span>
        </header>

        <section className="rr-moldroom__read">
          <Label>o que a leitura achou</Label>
          <p>{plural(r.shapes, 'forma de desenho', 'formas de desenho')}, {plural(r.fixed.length, 'texto igual em todos', 'textos iguais em todos')} e {plural(r.fields.length, 'campo', 'campos')}: {r.fields.map((f) => moldFieldLabel(f.field)).join(', ') || 'nenhum'}.</p>
          <small>fontes: {r.fonts.join(' · ') || 'nenhuma'}</small>
          <small>cores: {r.colors.join(' · ') || 'nenhuma'}</small>
          {r.fixed.length > 0 && <small>fica igual em toda peça: {r.fixed.map((t) => `"${t}"`).join(' · ')}</small>}
          {room.notices.map((n) => <Notice key={n}>{n}</Notice>)}
        </section>

        <section className="rr-moldroom__questions">
          <Label tone="accent">o que a leitura não tem como saber</Label>
          {!room.questions.length && <p className="rr-faint">nada a perguntar: os arquivos não deixaram dúvida.</p>}
          {room.questions.map((qn, n) => (
            <article key={qn.id} className="rr-moldroom__q">
              <span>{String(n + 1).padStart(2, '0')}</span>
              <div>
                <p>{qn.text}</p>
                {qn.seen && <small>nos arquivos: {qn.seen}</small>}
                <div className="rr-chip-row">
                  {qn.options.map((o) => <button key={o.value} type="button" className={cx('rr-chip', answered(qn) === o.value && 'rr-chip--on')} onClick={() => { setAnswers((a) => ({ ...a, [qn.id]: o.value })); setDirty(true); }}>{o.label}</button>)}
                </div>
                {!(qn.id in answers) && <small className="rr-faint">marcado o que os arquivos sugerem. confirme ou troque.</small>}
              </div>
            </article>
          ))}
          <label className="rr-moldroom__notes">
            <Label>o que mais esta peça tem de particular</Label>
            <Textarea rows={3} value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} placeholder="quando se usa este molde, o que nunca muda, o que o designer faria à mão num caso fora do comum" />
            <small className="rr-faint">vira o texto do bloco no BIS: é o que a equipe e o MODO leem sobre este molde.</small>
          </label>
          {save.isError && <Notice tone="error">{(save.error as Error).message}</Notice>}
          <div className="rr-moldroom__actions">
            <Button size="sm" variant={dirty ? 'primary' : 'quiet'} disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'refazendo o molde…' : dirty ? 'aplicar as respostas e refazer a prova' : 'confirmar as respostas'}</Button>
          </div>
        </section>
      </main>

      <aside className="rr-moldroom__proof">
        <Label>a prova</Label>
        <p className="rr-faint">à esquerda, o arquivo da agência. à direita, o molde preenchido pelo código com os mesmos dados. têm de ser iguais.</p>
        {r.examples.slice(0, previews.length || 3).map((ex, i) => (
          <div key={ex.file} className="rr-moldroom__pair">
            {previews[i]?.image ? <button type="button" onClick={() => setZoom(i)}><img src={previews[i]!.image!} alt={`original: ${ex.file}`} /></button> : <div className="rr-moldroom__noimg">sem imagem do original</div>}
            <ProofSvg svg={r.svg} values={ex.values} fontsKey={fontsKey} />
            <small>{ex.file}</small>
          </div>
        ))}
        <div className="rr-moldroom__test">
          <Label>teste com outro dado</Label>
          <p className="rr-faint">um nome comprido, alguém sem telefone: é aqui que o molde mostra se aguenta.</p>
          {r.fields.map((f) => <Input key={f.detected} value={test[f.field] ?? ''} onChange={(e) => setTest((t) => ({ ...t, [f.field]: e.target.value }))} placeholder={`${MOLD_FIELD_LABELS[f.field] ?? f.field}${f.values[0] ? `: ${f.values[0]}` : ''}`} />)}
          {testing && <ProofSvg svg={r.svg} values={test} fontsKey={fontsKey} />}
        </div>
        <div className="rr-moldroom__finish">
          {unnamed.length > 0 && <Notice>há texto que muda e ainda não tem nome: responda o que ele é.</Notice>}
          {dirty && <small className="rr-faint">há respostas que ainda não foram aplicadas à prova.</small>}
          {finish.isError && <Notice tone="error">{(finish.error as Error).message}</Notice>}
          <Button variant="primary" disabled={finish.isPending || dirty || unnamed.length > 0 || !r.fields.length} onClick={() => finish.mutate()}>{finish.isPending ? 'levando ao BIS…' : room.inBis ? 'atualizar o molde no BIS' : 'a prova confere: levar ao BIS'}</Button>
          {room.inBis && <small>este molde é um bloco do capítulo visual. <button type="button" className="rr-action" onClick={onBis}>ver em construir</button>. o MODO passa a usar quando a versão for publicada.</small>}
        </div>
      </aside>
      <Lightbox items={previews.map((p) => ({ src: p.image!, caption: p.file }))} index={zoom} onClose={() => setZoom(null)} onIndex={setZoom} />
    </>
  );
}

function ProofSvg({ svg, values, fontsKey }: { svg: string; values: Record<string, string>; fontsKey: number }) {
  // o mesmo preenchimento do MODO: se o texto não cabe aqui, não cabe lá
  const problem = useMemo(() => moldProblem(fillMold(svg, values)), [svg, values, fontsKey]);
  return (
    <div className={cx('rr-moldroom__svg', problem && 'rr-moldroom__svg--problem')}>
      <MoldSvg svg={svg} values={values} fontsKey={fontsKey} />
      {problem && <small>{problem}</small>}
    </div>
  );
}

